"use client";

import { ChevronDown, FileText } from "lucide-react";
import * as React from "react";
import { Card as V2Card, CardContent as V2CardContent } from "../atoms/card.js";
import { DisclosureButton } from "../atoms/disclosure-button.js";
import { cx } from "../internal/cx.js";
import { Collapsible, CollapsibleContent } from "../molecules/collapsible.js";
import { HeadingGroup } from "../molecules/heading-group.js";
import { InfoFieldRow as V2InfoFieldRow } from "../molecules/info-field-row.js";

/**
 * CompactBenefitsStatement — shared statement card with a summary band
 * and a data-table-style grid of expandable per-employee rows (header +
 * aligned columns + zebra striping).
 *
 * Used on the Owner Dashboard Billing Overview section AND on the Benefits
 * Wallet to summarize a monthly billing period. The component is purely
 * presentational: dates, premiums, and totals are pre-formatted by the
 * consuming app; the component owns the layout, expansion state, and
 * accessible toggle for each row.
 *
 * STATES per row: collapsed (default) and expanded (per-product detail).
 * The component is uncontrolled by default — pass `expandedIds` +
 * `onExpandedChange` to control it.
 *
 * Pulpo source references:
 *   apps/cockpit/src/components/owner-cockpit/CompactBenefitsStatement.tsx
 *   apps/cockpit/src/components/owner-cockpit/BenefitsStatementRow.tsx
 *   apps/cockpit/src/components/owner-cockpit/BenefitsStatementExpandedRow.tsx
 */

export type BenefitsStatementSmokerStatus = "smoker" | "non-smoker";

export interface BenefitsStatementProduct {
  /** Stable id. */
  id: string;
  /** Product name ("Medical PPO", "Dental"). */
  name: React.ReactNode;
  /** Coverage type ("Self only", "Family"). */
  coverageType?: React.ReactNode;
  /** Whether the employee elected smoker rates. */
  smokerStatus?: BenefitsStatementSmokerStatus;
  /** Pre-formatted monthly premium ("$182.50"). */
  premium: React.ReactNode;
}

export interface BenefitsStatementRowItem {
  /** Stable id. */
  id: string;
  /** Employee display name. */
  employeeName: React.ReactNode;
  /** Optional supporting line ("Coverage starts Jul 1"). */
  employeeMeta?: React.ReactNode;
  /** Pre-formatted total monthly premium for this employee. */
  monthlyPremium: React.ReactNode;
  /** Per-product detail rows shown when expanded. */
  products?: BenefitsStatementProduct[];
}

export interface CompactBenefitsStatementProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title" | "onChange"> {
  /** Card heading. Default "Compact benefits statement". */
  title?: React.ReactNode;
  /** Leading icon. Defaults to a Lucide FileText. Pass `null` to omit. */
  icon?: React.ReactNode | null;
  /** Column header above employee names (data-table style). Default "Employee". */
  employeeColumnHeader?: React.ReactNode;
  /** Column header above premium amounts. Default "Monthly premium". */
  premiumColumnHeader?: React.ReactNode;
  /** Statement period ("Jun 1 – Jun 30, 2026"). */
  period?: React.ReactNode;
  /** Total monthly premium across all rows. */
  totalAmount: React.ReactNode;
  /** Total enrolled employees. */
  enrolledCount?: React.ReactNode;
  /** Employee rows. */
  rows: BenefitsStatementRowItem[];
  /** Controlled expansion. */
  expandedIds?: string[];
  /** Default expanded ids (uncontrolled). */
  defaultExpandedIds?: string[];
  /** Controlled-mode expand callback. */
  onExpandedChange?: (ids: string[]) => void;
  /** Footer slot, typically a "See full benefits statement" link. */
  footer?: React.ReactNode;
}

function getMobileEmployeeStatus(meta: React.ReactNode): React.ReactNode {
  if (typeof meta !== "string") return meta;

  const value = meta.trim();
  if (!value) return null;

  const detailSeparator = " · ";
  const detailStart = value.lastIndexOf(detailSeparator);
  if (detailStart >= 0) {
    return value.slice(detailStart + detailSeparator.length).trim() || null;
  }

  const lowerValue = value.toLowerCase();
  if (
    lowerValue.includes("coverage") ||
    lowerValue.includes("election") ||
    lowerValue.includes("enrolled") ||
    lowerValue.includes("pending") ||
    lowerValue.includes("waived") ||
    lowerValue.includes("waiting")
  ) {
    return value;
  }

  return null;
}

export const CompactBenefitsStatement = React.forwardRef<
  HTMLDivElement,
  CompactBenefitsStatementProps
>(
  (
    {
      className,
      defaultExpandedIds,
      employeeColumnHeader = "Employee",
      enrolledCount,
      expandedIds,
      footer,
      icon,
      onExpandedChange,
      period,
      premiumColumnHeader = "Monthly premium",
      rows,
      title = "Compact benefits statement",
      totalAmount,
      ...props
    },
    ref,
  ) => {
    const isControlled = expandedIds !== undefined;
    const [internalExpanded, setInternalExpanded] = React.useState<string[]>(
      defaultExpandedIds ?? [],
    );
    const resolvedExpanded = isControlled ? (expandedIds ?? []) : internalExpanded;

    const toggle = (id: string) => {
      const isOpen = resolvedExpanded.includes(id);
      const next = isOpen ? resolvedExpanded.filter((x) => x !== id) : [...resolvedExpanded, id];
      if (!isControlled) setInternalExpanded(next);
      onExpandedChange?.(next);
    };

    const renderedIcon = icon === null ? null : (icon ?? <FileText aria-hidden size={18} />);

    return (
      <V2Card
        ref={ref}
        className={cx("cw-v2-compact-benefits-statement", className)}
        elevated
        {...props}
      >
        <V2CardContent>
          <div className="cw-v2-compact-benefits-statement__inner">
            <div className="cw-v2-compact-benefits-statement__header">
              {renderedIcon ? (
                <span className="cw-v2-compact-benefits-statement__icon" aria-hidden>
                  {renderedIcon}
                </span>
              ) : null}
              <HeadingGroup
                className="cw-v2-compact-benefits-statement__heading"
                headingLevel={3}
                title={title}
                variant="card"
              />
            </div>

            <div className="cw-v2-compact-benefits-statement__summary">
              {period ? <V2InfoFieldRow label="Period" layout="stacked" value={period} /> : null}
              {enrolledCount ? (
                <V2InfoFieldRow label="Enrolled" layout="stacked" value={enrolledCount} />
              ) : null}
              <V2InfoFieldRow
                className="cw-v2-compact-benefits-statement__summary-total"
                label="Total monthly"
                layout="stacked"
                value={totalAmount}
              />
            </div>

            <div className="cw-v2-compact-benefits-statement__data-table">
              <div className="cw-v2-compact-benefits-statement__thead">
                <div className="cw-v2-compact-benefits-statement__th">{employeeColumnHeader}</div>
                <div className="cw-v2-compact-benefits-statement__th cw-v2-compact-benefits-statement__th--end">
                  {premiumColumnHeader}
                </div>
                <div
                  className="cw-v2-compact-benefits-statement__th cw-v2-compact-benefits-statement__th--chevron"
                  aria-hidden
                />
              </div>
              <div className="cw-v2-compact-benefits-statement__tbody">
                {rows.map((row, rowIndex) => {
                  const expanded = resolvedExpanded.includes(row.id);
                  const expandable = (row.products?.length ?? 0) > 0;
                  const mobileEmployeeStatus = getMobileEmployeeStatus(row.employeeMeta);
                  return (
                    <div
                      key={row.id}
                      className={cx(
                        "cw-v2-compact-benefits-statement__row",
                        rowIndex % 2 === 1 && "cw-v2-compact-benefits-statement__row--alt",
                      )}
                    >
                      {expandable ? (
                        <DisclosureButton
                          className="cw-v2-compact-benefits-statement__row-trigger"
                          open={expanded}
                          onClick={() => toggle(row.id)}
                        >
                          <div className="cw-v2-compact-benefits-statement__cell cw-v2-compact-benefits-statement__cell--employee">
                            <span className="cw-v2-compact-benefits-statement__employee-name">
                              {row.employeeName}
                            </span>
                            {row.employeeMeta ? (
                              <span className="cw-v2-compact-benefits-statement__employee-meta">
                                {row.employeeMeta}
                              </span>
                            ) : null}
                            {mobileEmployeeStatus ? (
                              <span className="cw-v2-compact-benefits-statement__employee-mobile-status">
                                {mobileEmployeeStatus}
                              </span>
                            ) : null}
                          </div>
                          <div className="cw-v2-compact-benefits-statement__cell cw-v2-compact-benefits-statement__cell--premium">
                            <span className="cw-v2-compact-benefits-statement__mobile-label">
                              {premiumColumnHeader}
                            </span>
                            <span>{row.monthlyPremium}</span>
                          </div>
                          <div className="cw-v2-compact-benefits-statement__cell cw-v2-compact-benefits-statement__cell--chevron">
                            <ChevronDown
                              aria-hidden
                              className={cx(
                                "cw-v2-compact-benefits-statement__row-chevron",
                                expanded && "cw-v2-compact-benefits-statement__row-chevron--open",
                              )}
                              size={16}
                            />
                          </div>
                        </DisclosureButton>
                      ) : (
                        <DisclosureButton
                          className="cw-v2-compact-benefits-statement__row-trigger"
                          disabled
                        >
                          <div className="cw-v2-compact-benefits-statement__cell cw-v2-compact-benefits-statement__cell--employee">
                            <span className="cw-v2-compact-benefits-statement__employee-name">
                              {row.employeeName}
                            </span>
                            {row.employeeMeta ? (
                              <span className="cw-v2-compact-benefits-statement__employee-meta">
                                {row.employeeMeta}
                              </span>
                            ) : null}
                            {mobileEmployeeStatus ? (
                              <span className="cw-v2-compact-benefits-statement__employee-mobile-status">
                                {mobileEmployeeStatus}
                              </span>
                            ) : null}
                          </div>
                          <div className="cw-v2-compact-benefits-statement__cell cw-v2-compact-benefits-statement__cell--premium">
                            <span className="cw-v2-compact-benefits-statement__mobile-label">
                              {premiumColumnHeader}
                            </span>
                            <span>{row.monthlyPremium}</span>
                          </div>
                          <div className="cw-v2-compact-benefits-statement__cell cw-v2-compact-benefits-statement__cell--chevron" />
                        </DisclosureButton>
                      )}
                      {expandable ? (
                        <Collapsible open={expanded}>
                          <CollapsibleContent>
                            <div
                              className="cw-v2-compact-benefits-statement__row-details"
                              // biome-ignore lint/a11y/useSemanticElements: a `<section>`
                              // would create a landmark competing with the parent table; we
                              // intentionally use `<div role="region">` so screen readers
                              // hear the expansion as a generic region tied to the row.
                              role="region"
                            >
                              <div
                                className="cw-v2-compact-benefits-statement__product-chart"
                                aria-label="Per-product premiums"
                              >
                                {(row.products ?? []).map((product, productIndex) => (
                                  <div
                                    key={product.id}
                                    className={cx(
                                      "cw-v2-compact-benefits-statement__product-row",
                                      productIndex % 2 === 1 &&
                                        "cw-v2-compact-benefits-statement__product-row--alt",
                                    )}
                                  >
                                    <div className="cw-v2-compact-benefits-statement__product-cell">
                                      <span className="cw-v2-compact-benefits-statement__product-label">
                                        {product.name}
                                        {product.coverageType ? (
                                          <span className="cw-v2-compact-benefits-statement__product-meta">
                                            {" · "}
                                            {product.coverageType}
                                          </span>
                                        ) : null}
                                        {product.smokerStatus ? (
                                          <span className="cw-v2-compact-benefits-statement__product-meta">
                                            {" · "}
                                            {product.smokerStatus === "smoker"
                                              ? "Smoker rate"
                                              : "Non-smoker rate"}
                                          </span>
                                        ) : null}
                                      </span>
                                    </div>
                                    <div className="cw-v2-compact-benefits-statement__product-cell cw-v2-compact-benefits-statement__product-cell--amount">
                                      <span className="cw-v2-compact-benefits-statement__mobile-label">
                                        Premium
                                      </span>
                                      <span>{product.premium}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            {footer ? (
              <div className="cw-v2-compact-benefits-statement__footer">{footer}</div>
            ) : null}
          </div>
        </V2CardContent>
      </V2Card>
    );
  },
);

CompactBenefitsStatement.displayName = "CompactBenefitsStatement";
